import {mix, Eventable} from '../mixin';
import frameTimer from '../service/frameTimer';
import {getRandomInt, getPropertyDescriptor} from '../task';

const {Math} = globalThis;
const activeSyncers = [];

let defaultChangesPerSecond = 7;
let defaultMaxPhaseIndex = 5;

const activateSyncer = function(syncer)
{
    if (activeSyncers.indexOf(syncer) === -1)
    {
        activeSyncers.push(syncer);
        syncer._timerId = frameTimer.always(syncer.sync, syncer);
    }
};

const deactivateSyncer = function(syncer)
{
    const index = activeSyncers.indexOf(syncer);
    if (index !== -1)
    {
        activeSyncers.splice(index, 1);
        frameTimer.cancel(syncer._timerId);
    }
};

/** Optionen für den LipSyncer
 * @typedef {Object} LipSyncer~Options
 * @property {Object=} object Das Objekt, welches die Lippen enthält und das eine Methode hat, mit der man die
 * Lippen-Phase ändern kann.
 * @property {string=} setter Der Setter zur Änderung der Lippen-Phase (Möglich: Methode, Property, Setter).
 * Hieß früher ``method``, was aus Kompatibilitätgründen auch noch erkannt wird.
 * @property {number=} changesPerSecond Falls man den Default-Wert überschreiben will.
 * @property {number=} maxPhaseIndex Falls man den Default-Wert überschreiben will.
 * @property {number=} offset Offset in Sekunden. Z. B. sinnvoll, wenn man zu einer Lippenphase tweent und
 * das ``phaseChange`` früher feuern will.
 * @property {Array.<number>=} data Das Array der Lippen-Phasen
 */
class LipSyncer extends mix().with(Eventable)
{
    /** Setze den Default-Wert, wie oft pro Sekunde sich die Lippen bewegen soll. Voreingestellt ist ``7``.
     * @param {number} changes
     */
    static setChangesPerSecond(changes)
    {
        defaultChangesPerSecond = changes;
    }

    /** Setze den Default-Wert für den größten Index der Lippen-Phasen. Voreingestellt ist ``5``.
     * @param {number} key
     */
    static setMaxPhaseIndex(key)
    {
        defaultMaxPhaseIndex = key;
    }

    /** Eine Instanz von LipSyncer synchronisiert Lippen-Phasen mit der Zeit. Die Lippen-Phasen sind nullbasiete Indices und
     * entsprechen normalerweise Sprites in einem Spritesheet, wobei ``0`` für den geschlossenen Mund steht und jede weitere
     * Phase einer größeren Mundöffnung entspricht. Anhand einer Phasen-Reihe und der Anzahl der Lippen-Änderungen pro Sekunde
     * lässt sich jedes beliebige Sprite-Objekt animieren.<br /> Falls ein LipSyncer nicht mit dem AudioController zusammen
     * verwendet wird, sollte die play-Methode erst mit dem Event des Startens eines Audios aufgerufen werden.
     * @mixes Eventable
     * @param {LipSyncer~Options=} options
     * @example
     * import AudioController from '@kids/lib/class/AudioController';
     * import LipSyncer from '@kids/lib/class/LipSyncer';
     *
     * const lipData = {
     *     text1: [0,4,6,5,1,6,2],
     *     text2: [0,0,6,3,0,1,4]
     * };
     *
     * const syncer = new LipSyncer();
     *
     * syncer.on('audioPlay', function(evt)
     * {
     *     this.setData(lipData[evt.id]);
     * });
     *
     * syncer.on('phaseChange', function(evt)
     * {
     *     // Mache etwas mit evt.index - oder instanziiere den LipSyncer mit einem Objekt und einem Setter.
     *     console.log(evt.index);
     * });
     *
     * const controller = new AudioController('speech', './audio/speech');
     *
     * controller.on('loaded', function()
     * {
     *     this.play('text1', {lipSyncer: syncer, channel: 'blabla'});
     *     this.play('text2', {lipSyncer: syncer, channel: 'blabla', delay: 5});
     * });
     *
     * controller.loadManifest(['text1', 'text2']);
     */
    constructor(options = {})
    {
        super();
        this._data = options.data;
        this._object = options.object;

        this._setter = options.method || options.setter;
        this._setterType = undefined;
        if (this._setter)
        {
            this._setterType = 'prop';
            const setterDescriptor = getPropertyDescriptor(this._object, this._setter);
            if (typeof setterDescriptor.value === 'function')
            {
                this._setterType = 'func';
            }
        }

        this._maxPhaseIndex = options.maxPhaseIndex || defaultMaxPhaseIndex;
        this._intervalLength = 1000 / (options.changesPerSecond || defaultChangesPerSecond);
        this._offset = (options.offset || 0) * 1000;
        this._paused = false;
        this._playing = false;
        this._progress = 0;
        this._startTimestamp = 0;
        this._currentInterval = -1;
        this._timerId = 0;
    }

    /** Setter: Sets progress in seconds.
     * @param {number} progress
     */
    set progress(progress)
    {
        this._progress = progress * 1000;
        this._startTimestamp = Date.now() - this._progress;
    }

    /** Setze das Array der Lippen-Phasen
     * @param {Array.<number>} data
     * @return {LipSyncer}
     */
    setData(data)
    {
        this._data = data;
        return this;
    }

    /** Liefert die Lippen-Phase in einem bestimmten Interval
     * @param {number} interval
     * @return {number|undefined}
     */
    getPhaseAtInterval(interval)
    {
        if (this._data)
        {
            return this._data[interval];
        }
        return getRandomInt(0, this._maxPhaseIndex);
    }

    /** Setzt die Lippen-Phase mit der konfigurierten Objekt-Methode und feuert das Event phase.
     * @param {number} index
     * @return {LipSyncer}
     */
    setPhase(index)
    {
        if (this._object)
        {
            if (this._setterType === 'prop')
            {
                this._object[this._setter] = index;
            }
            else if (this._setterType === 'func')
            {
                this._object[this._setter](index);
            }
        }
        /**
         * @event LipSyncer#phaseChange
         * @property {number} index Die Lippen-Phase
         * @property {number} interval Der Fortschritt im Phasen-Array. ``-1`` wenn gestoppt.
         */
        this.fire('phaseChange', {index, interval: this._currentInterval});
        return this;
    }

    /** Startet das Iterieren der Lippen-Phasen.
     * @return {LipSyncer}
     */
    play()
    {
        if (this._playing) { return this; }
        if (this._paused)
        {
            this._startTimestamp = Date.now() - this._progress;
            this._paused = false;
        }
        else
        {
            this._startTimestamp = Date.now();
        }

        this._playing = true;
        activateSyncer(this);
        this.fire('play');
        return this;
    }

    /** Errechnet und setzt die aktuelle Lippenphase anhand eines Timestamp
     * @param {number} timestamp
     * @return {LipSyncer}
     */
    sync(timestamp)
    {
        if (this._startTimestamp === 0) { return this; }

        this._progress = timestamp - this._startTimestamp;
        let newInterval = Math.floor((this._progress - this._offset) / this._intervalLength);
        if (newInterval < 0)
        {
            newInterval = -1;
        }

        if (newInterval !== this._currentInterval)
        {
            this._currentInterval = newInterval;
            const phaseIndex = this.getPhaseAtInterval(newInterval);
            if (typeof phaseIndex === 'number')
            {
                this.setPhase(phaseIndex);
            }
            else
            {
                this.stop();
            }
        }
        return this;
    }

    /**
     * Pausiert das Iterieren der Lippen-Phasen.
     * @return {LipSyncer}
     */
    pause()
    {
        this._paused = true;
        this._progress = Date.now() - this._startTimestamp;
        this._startTimestamp = 0;
        this._currentInterval = -1;
        this._playing = false;

        this.setPhase(0);

        deactivateSyncer(this);
        this.fire('pause');
        return this;
    }

    /**
     * Stoppt das Iterieren der Lippen-Phasen.
     * @return {LipSyncer}
     */
    stop()
    {
        this.setPhase(0);

        this._paused = false;
        this._progress = 0;
        this._startTimestamp = 0;
        this._currentInterval = -1;
        this._playing = false;

        deactivateSyncer(this);
        this.fire('stop');
        return this;
    }

    /**
     * @return {boolean}
     */
    isPlaying()
    {
        return this._playing;
    }

    /**
     */
    unload()
    {
        this.stop();
        this.removeAllListeners();
        this._object = null;
        deactivateSyncer(this);
    }
}

export default LipSyncer;
